import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Terminal, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * RenewalProcessDialog - Terminal-like dialog showing renewal process
 * @param {boolean} open - Dialog open state
 * @param {function} onOpenChange - Callback when dialog state changes
 * @param {string} domain - Domain being renewed
 * @param {string} type - Type of renewal ('dry-run', 'live', or 'download')
 * @param {string} status - Current status ('processing', 'success', 'error')
 */
export function RenewalProcessDialog({ open, onOpenChange, domain, type, status }) {
    const [logs, setLogs] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);

    const isDryRun = type === 'dry-run';

    const processSteps = useMemo(() => {
        return isDryRun ? [
            { delay: 0, message: '🔧 Memulai simulasi pembaruan sertifikat...', type: 'info' },
            { delay: 800, message: `📡 Menghubungi server agent untuk domain: ${domain}`, type: 'info' },
            { delay: 1500, message: '🔍 Memvalidasi konfigurasi domain...', type: 'info' },
            { delay: 2200, message: '🌐 Memeriksa DNS records...', type: 'info' },
            { delay: 3000, message: '🔐 Menghubungi Let\'s Encrypt (staging)...', type: 'info' },
            { delay: 4000, message: '✅ Validasi domain berhasil', type: 'success' },
            { delay: 4800, message: '📝 Mensimulasikan pembuatan sertifikat...', type: 'info' },
        ] : type === 'download' ? [
            { delay: 0, message: '📥 Menyiapkan proses unduhan...', type: 'info' },
            { delay: 500, message: `📂 Memverifikasi kelengkapan file sertifikat untuk: ${domain}`, type: 'info' },
            { delay: 1200, message: '📦 Mengompresi file (fullchain, privkey, chain)...', type: 'info' },
            { delay: 2000, message: '🛡️ Melakukan pengecekan keamanan arsip...', type: 'info' },
        ] : type === 'update' ? [
            { delay: 0, message: '⚡ Memicu update cepat ke server agen...', type: 'info' },
            { delay: 800, message: `📡 Menghubungi server agent untuk domain: ${domain}`, type: 'info' },
            { delay: 1500, message: '🏃 Menjalankan pemindaian sertifikat lokal...', type: 'info' },
            { delay: 2200, message: '📤 Mengirimkan data terbaru ke Dashboard...', type: 'info' },
        ] : [
            { delay: 0, message: '🚀 Memulai pembaruan sertifikat LIVE...', type: 'warning' },
            { delay: 800, message: `📡 Menghubungi server agent untuk domain: ${domain}`, type: 'info' },
            { delay: 1500, message: '🔍 Memvalidasi konfigurasi domain...', type: 'info' },
            { delay: 2200, message: '🌐 Memeriksa DNS records...', type: 'info' },
            { delay: 3200, message: '🔐 Menghubungi Let\'s Encrypt (production)...', type: 'info' },
            { delay: 4500, message: '✅ Validasi domain berhasil', type: 'success' },
            { delay: 5200, message: '📝 Membuat sertifikat baru...', type: 'info' },
            { delay: 6500, message: '💾 Menyimpan sertifikat ke server...', type: 'info' },
            { delay: 7500, message: '🔄 Memuat ulang konfigurasi web server...', type: 'info' },
        ];
    }, [isDryRun, domain, type]);

    useEffect(() => {
        if (open && status === 'processing') {
            // Reset logs when dialog opens
            setLogs([]);
            setCurrentStep(0);

            // Add logs progressively
            processSteps.forEach((step, index) => {
                setTimeout(() => {
                    setLogs(prev => [...prev, step]);
                    setCurrentStep(index + 1);
                }, step.delay);
            });
        }
    }, [open, status, processSteps]);

    // Add final status log
    useEffect(() => {
        if (status === 'success') {
            setTimeout(() => {
                let successMessage = '✨ Proses selesai dengan sukses!';
                if (isDryRun) successMessage = '🎯 Simulasi selesai - tidak ada perubahan pada sistem';
                else if (type === 'download') successMessage = '✅ Paket sertifikat siap diunduh';
                else if (type === 'update') successMessage = '🔄 Sinkronisasi data ke Dashboard berhasil';
                else successMessage = '🎉 Pembaruan sertifikat berhasil!';

                setLogs(prev => [...prev, {
                    message: successMessage,
                    type: 'success'
                }]);
            }, 500);
        } else if (status === 'error') {
            setTimeout(() => {
                setLogs(prev => [...prev, {
                    message: '❌ Proses gagal. Periksa detail error di atas.',
                    type: 'error'
                }]);
            }, 500);
        }
    }, [status]);

    const getLogColor = (type) => {
        switch (type) {
            case 'success':
                return 'text-green-400';
            case 'error':
                return 'text-red-400';
            case 'warning':
                return 'text-yellow-400';
            default:
                return 'text-blue-400';
        }
    };

    const getStatusIcon = () => {
        if (status === 'processing') {
            return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
        } else if (status === 'success') {
            return <CheckCircle2 className="h-5 w-5 text-green-500" />;
        } else if (status === 'error') {
            return <XCircle className="h-5 w-5 text-red-500" />;
        }
        return <Terminal className="h-5 w-5 text-muted-foreground" />;
    };

    const getStatusText = () => {
        if (status === 'processing') {
            if (type === 'download') return 'Sedang Menyiapkan Unduhan...';
            if (type === 'update') return 'Sedang Mensinkronkan Data...';
            return isDryRun ? 'Sedang Menjalankan Uji Pembaruan...' : 'Sedang Memperbarui Sertifikat...';
        } else if (status === 'success') {
            return 'Proses Berhasil';
        } else if (status === 'error') {
            return 'Proses Gagal';
        }
        if (type === 'download') return 'Proses Unduhan';
        if (type === 'update') return 'Proses Sinkronisasi';
        return 'Proses Pembaruan';
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {getStatusIcon()}
                        <span>{getStatusText()}</span>
                    </DialogTitle>
                    <DialogDescription>
                        {type === 'download' ? 'Persiapan unduhan sertifikat' : type === 'update' ? 'Sinkronisasi instan data' : isDryRun ? 'Simulasi pembaruan' : 'Pembaruan live'} untuk domain: <span className="font-semibold">{domain}</span>
                    </DialogDescription>
                </DialogHeader>

                {/* Terminal-like output */}
                <div className="flex-1 overflow-hidden">
                    <div className="bg-slate-950 rounded-lg border border-slate-800 p-4 h-full overflow-y-auto font-mono text-sm">
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-800">
                            <Terminal className="h-4 w-4 text-green-400" />
                            <span className="text-green-400">terminal@ssl-monitor</span>
                            <span className="text-slate-500">~</span>
                        </div>

                        <AnimatePresence>
                            {logs.map((log, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className={`mb-2 ${getLogColor(log.type)}`}
                                >
                                    <span className="text-slate-500 mr-2">[{new Date().toLocaleTimeString()}]</span>
                                    {log.message}
                                </motion.div>
                            ))}
                        </AnimatePresence>

                        {/* Blinking cursor when processing */}
                        {status === 'processing' && (
                            <motion.div
                                className="inline-block w-2 h-4 bg-green-400 ml-1"
                                animate={{ opacity: [1, 0, 1] }}
                                transition={{ duration: 1, repeat: Infinity }}
                            />
                        )}
                    </div>
                </div>

                {/* Progress indicator */}
                {status === 'processing' && (
                    <div className="mt-4">
                        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                            <span>Progress</span>
                            <span>{Math.min(100, Math.round((currentStep / processSteps.length) * 100))}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <motion.div
                                className="h-full bg-blue-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.min(100, (currentStep / processSteps.length) * 100)}%` }}
                                transition={{ duration: 0.5 }}
                            />
                        </div>
                    </div>
                )}

                {/* Status message */}
                {status !== 'processing' && (
                    <div className="mt-4 text-center">
                        {status === 'success' ? (
                            <p className="text-sm text-green-600 dark:text-green-400">
                                ✅ Proses berhasil diselesaikan
                            </p>
                        ) : status === 'error' ? (
                            <p className="text-sm text-red-600 dark:text-red-400">
                                ❌ Terjadi kesalahan saat memproses
                            </p>
                        ) : null}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
